import {
  ChatAttachIcon,
  ChatCloseIcon,
  ChatMicIcon,
  ChatSendIcon,
} from '../../../assets/svg';
// Direct file imports, not the styles barrel - see CLAUDE.md (the barrel
// causes a "Cannot access 'Box' before initialization" circular init).
import { Div, StatusMark } from '../../../styles/styles';
import { Disclaimer } from '../../../styles/text';
import { lightTheme } from '../../../styles/theme';
import { ConversationMessageWithStatus } from '../../../types/conversation.types';
import {
  ComposerBar,
  ComposerIconButton,
  ComposerTextarea,
  ComposerWrapper,
  ReplyStrip,
  ReplyStripBody,
  ReplyStripClose,
  SendButton,
} from '../chatSurfaces.styled';
import {
  hasMentionAll,
  MentionCandidate,
  resolveMentions,
} from '../helpers/chatMentions';
import { messagePreview, roleLabel } from '../helpers/chatMessage';
import { useComposerDraft } from '../hooks/useComposerDraft';
import { useMentions } from '../hooks/useMentions';
import {
  MentionItem,
  MentionMenu,
  MentionName,
  MentionRole,
  MentionText,
  StopGlyph,
  TypingLine,
} from './ChatInput.styled';
import { ConversationAvatar } from './ConversationAvatar';

export type { MentionCandidate };

/** Matches the backend's `body` maxLength; the image editor's caption too. */
export const MAX_BODY_LENGTH = 5000;

export type ChatInputSubmit = {
  body: string;
  replyToId?: number;
  /** Resolved from `@Name` runs in the body against `mentionCandidates`. */
  mentionUserIds?: number[];
  /** The body still says `@all`, in a composer that offers it. */
  mentionAll?: boolean;
};

type Props = {
  onSubmit: (payload: ChatInputSubmit) => void;
  disabled?: boolean;
  /** Set while editing an existing message rather than composing a new one. */
  editing?: ConversationMessageWithStatus | null;
  onCancelEdit?: () => void;
  onSubmitEdit?: (body: string) => void;
  replyTo?: ConversationMessageWithStatus | null;
  onCancelReply?: () => void;
  /** Wired in by the attachments layer; the buttons hide when absent. */
  onPickFiles?: () => void;
  onStartRecording?: () => void;
  /** Staged attachment previews / recorder UI render above the bar. */
  children?: React.ReactNode;
  /** What the empty field says. The dispatcher thread names who it goes to. */
  placeholder?: string;
  /** Blocks send while uploads are still running. */
  busy?: boolean;
  /** True when there are staged attachments, so an empty body can still send. */
  hasAttachments?: boolean;
  /**
   * Set to block sending and explain why, e.g. a staged attachment failed to
   * upload. Without this, sending would go through with the failed file
   * silently dropped - the sender would believe they had sent it.
   */
  blockedReason?: string;
  /**
   * Members who can be mentioned. Empty or absent turns the `@` menu off,
   * which is what a support thread wants - there is nobody to mention there.
   */
  mentionCandidates?: MentionCandidate[];
  /**
   * Offers `@all` at the top of the `@` menu and sends `mentionAll` when the
   * body says it. Groups only - anywhere else the backend ignores the flag.
   */
  mentionAll?: boolean;
  /** Names of the people typing right now, rendered above the bar. */
  typingNames?: string[];
  /**
   * Set while Viktor is writing an answer. Send becomes Stop for as long as
   * it is set: a question sent now would interrupt the answer being written
   * (Viktor then answers both at once), so the composer offers the one thing
   * that is safe - calling the answer off. The draft stays; Enter waits.
   */
  onStop?: () => void;
  /** Stop has been pressed and the socket has not yet confirmed it. */
  stopping?: boolean;
  /** Called on every keystroke so the socket can broadcast typing state. */
  onTyping?: (typing: boolean) => void;
  /**
   * The conversation being composed in. Changing it puts the cursor in the
   * field, so opening a thread on a desktop is ready to type in.
   */
  autoFocusKey?: number | string | null;
};

/** "Ana is typing..." / "Ana, Ivan and others are typing...". */
const typingSentence = (names: string[]) =>
  names.length === 1
    ? `${names[0]} is typing...`
    : `${names.slice(0, 2).join(', ')}${
        names.length > 2 ? ' and others' : ''
      } are typing...`;

export const ChatInput = ({
  onSubmit,
  disabled,
  editing,
  onCancelEdit,
  onSubmitEdit,
  replyTo,
  onCancelReply,
  onPickFiles,
  onStartRecording,
  children,
  placeholder = 'Type your message',
  busy,
  hasAttachments,
  blockedReason,
  mentionCandidates,
  mentionAll,
  typingNames,
  onStop,
  stopping,
  onTyping,
  autoFocusKey,
}: Props) => {
  const { value, setValue, textareaRef } = useComposerDraft({
    editing,
    replyTo,
    onTyping,
    autoFocusKey,
  });

  const mentions = useMentions({
    candidates: mentionCandidates,
    withAll: mentionAll,
    value,
    onChange: setValue,
    textareaRef,
  });

  const trimmed = value.trim();
  // `blockedReason` is ignored while editing: an edit only touches the body,
  // so a broken staged attachment has no bearing on it.
  const canSubmit =
    !disabled &&
    !busy &&
    !onStop &&
    (editing || !blockedReason) &&
    (trimmed.length > 0 || (!editing && hasAttachments));

  const submit = () => {
    if (!canSubmit) return;

    if (editing) {
      onSubmitEdit?.(trimmed);
    } else {
      onSubmit({
        body: trimmed,
        replyToId: replyTo?.id,
        mentionUserIds: mentionCandidates?.length
          ? resolveMentions(trimmed, mentionCandidates)
          : undefined,
        /* Read off the text, not off the menu: a `@all` deleted before
           sending must not notify the group, and one typed by hand must. */
        mentionAll: (mentionAll && hasMentionAll(trimmed)) || undefined,
      });
    }
    setValue('');
    mentions.close();
    onTyping?.(false);
    /*
     * Stay in the field. On a phone this runs inside the tap that sent the
     * message, which is the only moment focus can be taken without a second
     * gesture - so the keyboard never drops between two messages. On a desktop
     * it is what lets you keep typing after clicking the arrow.
     */
    textareaRef.current?.focus();
  };

  const onKeyDown = (event: React.KeyboardEvent<HTMLTextAreaElement>) => {
    /* While the mention menu is open it owns the keys that would otherwise
       send the message - picking a name with Enter must not also post it. */
    if (mentions.onKeyDown(event)) return;

    // Enter sends, Shift+Enter breaks the line.
    if (event.key === 'Enter' && !event.shiftKey) {
      event.preventDefault();
      submit();
    }
    if (event.key === 'Escape') {
      if (editing) onCancelEdit?.();
      else if (replyTo) onCancelReply?.();
    }
  };

  if (disabled) {
    return (
      <ComposerWrapper>
        <Disclaimer $m="0" $center $color={lightTheme.lightGray}>
          You have read-only access to this conversation.
        </Disclaimer>
      </ComposerWrapper>
    );
  }

  const quoted = editing ?? (replyTo || null);

  return (
    <ComposerWrapper>
      {quoted && (
        <ReplyStrip>
          <StatusMark $color={lightTheme.primary} />
          <ReplyStripBody>
            <b>
              {editing ? 'Editing message' : quoted.sender?.name ?? 'Message'}
            </b>
            <span>{messagePreview(quoted)}</span>
          </ReplyStripBody>
          <ReplyStripClose onClick={editing ? onCancelEdit : onCancelReply}>
            <ChatCloseIcon />
          </ReplyStripClose>
        </ReplyStrip>
      )}

      {children}

      {blockedReason && !editing && (
        <Div $mb="8">
          <Disclaimer $m="0" $color={lightTheme.primary}>
            {blockedReason}
          </Disclaimer>
        </Div>
      )}

      {!!typingNames?.length && (
        <TypingLine>{typingSentence(typingNames)}</TypingLine>
      )}

      {mentions.matches.length > 0 && (
        <MentionMenu>
          {mentions.matches.map((option, index) => (
            <MentionItem
              key={option.kind === 'all' ? 'all' : option.candidate.id}
              type="button"
              $active={index === mentions.index}
              onMouseDown={(event) => {
                // mousedown, not click: the textarea must not lose focus
                // before the insertion runs.
                event.preventDefault();
                mentions.apply(option);
              }}
            >
              {option.kind === 'all' ? (
                <>
                  {/* The group's own avatar: this row is the group. */}
                  <ConversationAvatar name="all" type="group" size={28} />
                  <MentionText>
                    <MentionName>all</MentionName>
                    <MentionRole>Notify everyone in this group</MentionRole>
                  </MentionText>
                </>
              ) : (
                <>
                  {/* Initials rather than a photograph: nobody in the system
                      has one, and a row of identical placeholder heads is
                      worse than no picture at all. */}
                  <ConversationAvatar name={option.candidate.name} size={28} />
                  <MentionText>
                    <MentionName>{option.candidate.name}</MentionName>
                    {!!roleLabel(option.candidate.role) && (
                      <MentionRole>
                        {roleLabel(option.candidate.role)}
                      </MentionRole>
                    )}
                  </MentionText>
                </>
              )}
            </MentionItem>
          ))}
        </MentionMenu>
      )}

      <ComposerBar>
        <ComposerTextarea
          ref={textareaRef}
          rows={1}
          value={value}
          maxLength={MAX_BODY_LENGTH}
          placeholder={placeholder}
          onChange={(event) => {
            setValue(event.target.value);
            mentions.trackCaret(
              event.target.value,
              event.target.selectionStart ?? 0
            );
          }}
          onBlur={mentions.close}
          onKeyDown={onKeyDown}
        />

        {!editing && onPickFiles && (
          <ComposerIconButton
            type="button"
            aria-label="Attach a file"
            onClick={onPickFiles}
            disabled={busy}
          >
            <ChatAttachIcon />
          </ComposerIconButton>
        )}

        {!editing && onStartRecording && (
          <ComposerIconButton
            type="button"
            aria-label="Record a voice message"
            onClick={onStartRecording}
            disabled={busy}
          >
            <ChatMicIcon />
          </ComposerIconButton>
        )}

        {onStop ? (
          /* The same disc, wearing a square: one control in one place, so the
             bar never shifts as an answer starts and ends. Red while it can
             be pressed - it IS the primary action for as long as it shows. */
          <SendButton
            type="button"
            aria-label="Stop vGrubs AI"
            onMouseDown={(event) => event.preventDefault()}
            onClick={onStop}
            disabled={stopping}
          >
            <StopGlyph />
          </SendButton>
        ) : (
          <SendButton
            type="button"
            aria-label={editing ? 'Save changes' : 'Send message'}
            /*
             * Keeps the phone keyboard up between messages. Pressing a button
             * moves focus to it, which closes the keyboard, and the
             * conversation jumps back to full height for the moment it takes
             * to tap the field again - so a reply of three short lines means
             * three keyboards. The default is cancelled before focus ever
             * moves; the textarea never loses it, so nothing has to be given
             * back.
             *
             * Only the mouse press - a touch that is prevented here never
             * produces the click that sends the message. The phone's own case
             * is answered inside `submit`, which puts focus back while still
             * in the gesture that opened the keyboard.
             */
            onMouseDown={(event) => event.preventDefault()}
            onClick={submit}
            disabled={!canSubmit}
          >
            {/* Always white - the disc under it is what changes colour. */}
            <ChatSendIcon color={lightTheme.white} />
          </SendButton>
        )}
      </ComposerBar>
    </ComposerWrapper>
  );
};
